import React, { useState, useRef, useEffect } from 'react';
import { AutoCompleteSuggestions } from './components/AutoCompleteSuggestions';
import { SearchInput } from './components/SearchInput';
import { useAutocomplete } from './SearchBar.hooks';
import { SearchInformation } from '@pages/SearchPage';
import './SearchBar.scss';

interface SearchBarProps {
  query?: string;
  onSubmit: (value: string) => void;
  placeholder: string;
  searchInformation?: SearchInformation[];
  className?: string;
  showSearchIcon?: boolean;
  searchLastUpdated?: string;
}

export const SearchBar = ({
  query = '',
  onSubmit,
  placeholder,
  searchInformation,
  className,
  showSearchIcon = true,
  searchLastUpdated,
}: SearchBarProps) => {
  const inputRef = useRef(null);
  const [value, setValue] = useState(query);
  const [autocompleteFrom, setAutocompleteFrom] = useState('');

  useEffect(() => {
    // if query is updated from parent then update value
    setValue(query);
  }, [query]);

  const { onKeyDown, suggestions, selectedSuggestion, handleAutocomplete, resetSuggestions } =
    useAutocomplete({
      query,
      value,
      setValueToSuggestion: (suggestion) => {
        setValue(suggestion);
      },
      onEnterKeyPress: () => {
        onSubmit(value);
      },
      onEscKeyPress: () => {
        inputRef.current.blur();
      },
    });

  const onChangeInput = ({ target }) => {
    setValue(target.value);
    setAutocompleteFrom(target.value);
    handleAutocomplete(target.value);
    resetSuggestions();
  };

  const onClearBtnClick = () => {
    setValue('');
    inputRef.current.focus();
  };

  const onAutocompleteSelection = (suggestion) => {
    setValue(suggestion);
    onSubmit(suggestion);
  };

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    // unmount suggestions when results are fetched
    resetSuggestions();
  }, [searchLastUpdated]);

  return (
    <div className={className}>
      <div className='autocomplete-container'>
        <SearchInput
          showSearchIcon={showSearchIcon}
          ref={inputRef}
          placeholder={placeholder}
          value={value}
          onClearBtnClick={onClearBtnClick}
          onKeyDown={onKeyDown}
          onChange={onChangeInput}
        />

        {searchInformation?.length > 0 && (
          <div className='l-search-panel-links'>
            {searchInformation.map((item) => (
              <span key={item.Text}>
                <a href={item.Url}>{item.Text}</a>
              </span>
            ))}
          </div>
        )}

        {suggestions.length > 0 && (
          <AutoCompleteSuggestions
            suggestions={suggestions}
            autocompleteFrom={autocompleteFrom}
            selectedSuggestion={selectedSuggestion}
            onSelection={onAutocompleteSelection}
          />
        )}
      </div>
    </div>
  );
};
